import type { EditorConfig, LexicalNode, NodeKey, SerializedLexicalNode, Spread } from 'lexical';
import { $getNodeByKey, DecoratorNode } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import type { ReactElement } from 'react';
import { createElement, useCallback, useRef, useEffect, useState } from 'react';

export type SerializedSpeakerNoteNode = Spread<
  { type: 'speaker-note'; version: 1; text: string },
  SerializedLexicalNode
>;

function SpeakerNoteDisplay({ text, nodeKey }: { text: string; nodeKey: string }) {
  const [editor] = useLexicalComposerContext();
  const [value, setValue] = useState(text);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setValue(text);
  }, [text]);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newText = e.target.value;
      setValue(newText);
      editor.update(() => {
        const node = $getNodeByKey(nodeKey);
        if (node && $isSpeakerNoteNode(node)) {
          node.setText(newText);
        }
      });
    },
    [editor, nodeKey],
  );

  return createElement(
    'div',
    {
      style: {
        borderLeft: '3px solid #79628c',
        background: 'rgba(121, 98, 140, 0.08)',
        padding: '8px 12px',
        borderRadius: '0 4px 4px 0',
        display: 'flex',
        alignItems: 'center',
        gap: '6px',
      },
    },
    createElement(
      'span',
      {
        style: {
          fontWeight: 600,
          fontSize: '0.75rem',
          textTransform: 'uppercase' as const,
          letterSpacing: '0.04em',
          color: '#79628c',
          flexShrink: 0,
        },
      },
      'Note:',
    ),
    createElement('input', {
      ref: inputRef,
      type: 'text',
      value,
      onChange: handleChange,
      style: {
        flex: 1,
        border: 'none',
        background: 'transparent',
        fontStyle: 'italic',
        fontSize: '0.875rem',
        opacity: 0.85,
        outline: 'none',
        padding: 0,
        fontFamily: 'inherit',
        color: 'inherit',
      },
      placeholder: 'Type your note here...',
    }),
  );
}

export class SpeakerNoteNode extends DecoratorNode<ReactElement> {
  __text: string;

  static getType(): string {
    return 'speaker-note';
  }

  static clone(node: SpeakerNoteNode): SpeakerNoteNode {
    return new SpeakerNoteNode(node.__text, node.__key);
  }

  constructor(text: string, key?: NodeKey) {
    super(key);
    this.__text = text;
  }

  getText(): string {
    return this.__text;
  }

  setText(text: string): void {
    const self = this.getWritable();
    self.__text = text;
  }

  createDOM(_config: EditorConfig): HTMLElement {
    const div = document.createElement('div');
    div.setAttribute('data-speaker-note', 'true');
    div.setAttribute('contenteditable', 'false');
    div.style.userSelect = 'none';
    div.style.margin = '8px 0';
    return div;
  }

  updateDOM(): false {
    return false;
  }

  static importJSON(serializedNode: SerializedSpeakerNoteNode): SpeakerNoteNode {
    return $createSpeakerNoteNode(serializedNode.text);
  }

  exportJSON(): SerializedSpeakerNoteNode {
    return {
      type: 'speaker-note',
      version: 1,
      text: this.__text,
    };
  }

  getTextContent(): string {
    if (!this.__text) return '\n';
    return `> ${this.__text}\n`;
  }

  isInline(): false {
    return false;
  }

  decorate(): ReactElement {
    return createElement(SpeakerNoteDisplay, { text: this.__text, nodeKey: this.__key });
  }
}

export function $createSpeakerNoteNode(text: string): SpeakerNoteNode {
  return new SpeakerNoteNode(text);
}

export function $isSpeakerNoteNode(node: LexicalNode | null | undefined): node is SpeakerNoteNode {
  return node instanceof SpeakerNoteNode;
}
